import React, { useState, useMemo } from "react";
import { connect } from "react-redux";
import { RootState, useAppThunkDispatch } from "redux/store";
import { DashboardLayout } from "component/layout/dashboard";
import * as Mui from "@mui/material";
import * as MuiLab from "@mui/material";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import { AppCard } from "component/shared/app-card";
import { createMarket } from "redux/slice/markets";
import { createCampaign } from "redux/slice/campaigns";
import { createCampaignMessage } from "redux/slice/campaign-messages";
import { createCampaignCampaignMessage } from "redux/slice/campaign-campaign-messages";
import { EmailMarketingType } from "type/email-marketing-type";
import { CampaignMessageType } from "type/campaign-message";
import { TimeUnitType } from "type/time-unit";
import { CampaignMessageStatus } from "type/campaign-message-status";
import { DynamicContentType } from "type/dynamic-content";
import { CampaignStatus } from "type/campaign-status";
import { NewCampaign } from "model/campaign";
import { NewCampaignMessage } from "model/campaign-message";

type RunStatus = "idle" | "confirming" | "running" | "done" | "error";

const PRIMARY = "#36747B";
const DIVIDER = "#DDE7E8";

function parseZips(raw: string): string[] {
	return Array.from(
		new Set(
			raw
				.split(/[\n,\s]+/)
				.map((z) => z.trim())
				.filter((z) => /^\d{5}$/.test(z))
		)
	);
}

interface Props {
	reduxState: RootState;
}

const Component: React.FC<Props> = ({ reduxState }) => {
	const dispatch = useAppThunkDispatch();

	const allCampaigns = useMemo(
		() => Object.values(reduxState.campaigns.byId),
		[reduxState.campaigns.byId]
	);
	const campaignCampaignMsgById = reduxState.campaignCampaignMessages.byId;
	const campaignMsgById = reduxState.campaignMessages.byId;

	const [zipInput, setZipInput] = useState("");
	const [zips, setZips] = useState<string[]>([]);
	const [zipNames, setZipNames] = useState<Record<string, string>>({});
	const [selectedCampaignIds, setSelectedIds] = useState<Set<number>>(new Set());
	const [filterTerm, setFilterTerm] = useState("");
	const [runStatus, setRunStatus] = useState<RunStatus>("idle");
	const [progress, setProgress] = useState(0);
	const [currentOp, setCurrentOp] = useState("");
	const [errorMsg, setErrorMsg] = useState<string | null>(null);
	const [completedCount, setCompletedCount] = useState(0);

	const visibleCampaigns = useMemo(
		() =>
			allCampaigns.filter((c) =>
				c.name.toLowerCase().includes(filterTerm.toLowerCase())
			),
		[allCampaigns, filterTerm]
	);

	const selectedCampaigns = useMemo(
		() => allCampaigns.filter((c) => selectedCampaignIds.has(c.id)),
		[allCampaigns, selectedCampaignIds]
	);

	const totalPairs = zips.length * selectedCampaignIds.size;
	const canRun = zips.length > 0 && selectedCampaignIds.size > 0;

	const previewPairs = useMemo(() => {
		const pairs: { zip: string; src: string }[] = [];
		for (const zip of zips) {
			for (const c of selectedCampaigns) {
				pairs.push({ zip, src: c.name });
			}
		}
		return pairs;
	}, [zips, selectedCampaigns]);

	const handleZipChange = (value: string) => {
		setZipInput(value);
		const parsed = parseZips(value);
		setZips(parsed);
		setZipNames((prev) => {
			const next: Record<string, string> = {};
			parsed.forEach((z) => { next[z] = prev[z] ?? ""; });
			return next;
		});
	};

	const handleZipNameChange = (zip: string, name: string) => {
		setZipNames((prev) => ({ ...prev, [zip]: name }));
	};

	const removeZip = (zip: string) => {
		const next = zips.filter((z) => z !== zip);
		setZips(next);
		setZipInput(next.join("\n"));
		setZipNames((prev) => {
			const next2 = { ...prev };
			delete next2[zip];
			return next2;
		});
	};

	const toggleCampaign = (id: number) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const toggleAll = () => {
		const visibleIds = visibleCampaigns.map((c) => c.id);
		const allVisible = visibleIds.every((id) => selectedCampaignIds.has(id));
		setSelectedIds((prev) => {
			const next = new Set(prev);
			visibleIds.forEach((id) =>
				allVisible ? next.delete(id) : next.add(id)
			);
			return next;
		});
	};

	const reset = () => {
		setZipInput("");
		setZips([]);
		setSelectedIds(new Set());
		setFilterTerm("");
		setRunStatus("idle");
		setProgress(0);
		setCurrentOp("");
		setErrorMsg(null);
		setCompletedCount(0);
		setZipNames({});
	};

	const runBulkCreation = async () => {
		setRunStatus("running");
		setProgress(0);
		setErrorMsg(null);

		let completed = 0;
		let op = "";
		const setOp = (s: string) => {
			op = s;
			setCurrentOp(s);
		};

		try {
			for (const zip of zips) {
				const marketName = zipNames[zip]?.trim() || `Zip ${zip}`;
				setOp(`Creating market "${marketName}"…`);
				const marketResult = await dispatch(
					createMarket({
						market: {
							name: marketName,
							criteria: [{ name: "zip", label: "Zip", value: [zip] }],
							inactive: false,
						},
					})
				).unwrap();
				const newMarketId = marketResult.id;

				for (const srcCampaign of selectedCampaigns) {
					setOp(`Cloning "${srcCampaign.name}" into Zip ${zip}…`);

					const emailMarketingType = EmailMarketingType.getById(
						srcCampaign.emailMarketingTypeId
					);
					if (!emailMarketingType) {
						throw new Error(
							`Unknown emailMarketingTypeId: ${srcCampaign.emailMarketingTypeId}`
						);
					}

					const newCampaign: NewCampaign = {
						name: `${srcCampaign.name} - ${zip}`,
						marketId: newMarketId,
						emailMarketingType,
						description: srcCampaign.description,
						campaignType: srcCampaign.campaignType
							? DynamicContentType.getById(srcCampaign.campaignType)
							: undefined,
						emailBrandingYn: srcCampaign.emailBrandingYn,
						campaignCategoryId: srcCampaign.campaignCategoryId,
						inactiveYn: srcCampaign.inactiveYn,
						status: srcCampaign.status
							? CampaignStatus.getById(srcCampaign.status)
							: undefined,
					};

					const campaignResult = await dispatch(
						createCampaign({ campaign: newCampaign })
					).unwrap();
					const newCampaignId = campaignResult.id;

					const sourceCCMs = Object.values(campaignCampaignMsgById)
						.filter((ccm) => ccm.campaignId === srcCampaign.id)
						.sort((a, b) => a.messageOrder - b.messageOrder);

					if (sourceCCMs.length > 0) {
						setOp(
							`Copying ${sourceCCMs.length} message template(s) for "${srcCampaign.name} - ${zip}"…`
						);
					}

					for (const ccm of sourceCCMs) {
						const srcMsg = campaignMsgById[ccm.campaignMessageId];
						if (!srcMsg) continue;

						const newMsg: NewCampaignMessage = {
							name: srcMsg.name,
							subject: srcMsg.subject,
							htmlContent: srcMsg.htmlContent,
							jsonContent: srcMsg.jsonContent,
							messageType: CampaignMessageType.getById(srcMsg.messageType),
							timeToSendUnit: TimeUnitType.getById(srcMsg.timeToSendUnit),
							timeToSendNumber: srcMsg.timeToSendNumber,
							status: CampaignMessageStatus.getById(srcMsg.status),
							ownedByReseller: srcMsg.ownedByReseller,
							isInactiveYn: srcMsg.isInactiveYn,
							blast: srcMsg.blast,
						};

						const msgResult = await dispatch(
							createCampaignMessage({ campaignMessage: newMsg })
						).unwrap();

						await dispatch(
							createCampaignCampaignMessage({
								campaignCampaignMessage: {
									campaignId: newCampaignId,
									campaignMessageId: msgResult.id,
									messageOrder: ccm.messageOrder,
								},
							})
						).unwrap();
					}

					completed++;
					setProgress(completed);
				}
			}

			setCompletedCount(completed);
			setRunStatus("done");
		} catch(err) {
			const msg = err instanceof Error ? err.message : String(err);
			setErrorMsg(`Failed during: ${op}\n\n${msg}`);
			setCompletedCount(completed);
			setRunStatus("error");
		}
	};

	const pageHeader = (
		<Mui.Typography variant="h1">
			<FeatherIcon>
				<Icons.Layers />
			</FeatherIcon>
			Zip Code Campaign Bulk-Creation
		</Mui.Typography>
	);

	// ── Running / Done / Error ───────────────────────────────────────────────
	if (runStatus === "running" || runStatus === "done" || runStatus === "error") {
		return (
			<DashboardLayout permitted={true} title="Zip Campaign Utility" header={pageHeader}>
				<Mui.Container maxWidth="md">
					<Mui.Box pt={3} pb={8}>
						{runStatus === "running" && (
							<AppCard>
								<Mui.Box p={3}>
									<Mui.Typography variant="subtitle1" gutterBottom style={{ fontWeight: 600 }}>
										Running…
									</Mui.Typography>
									<Mui.Typography variant="body2" color="textSecondary" gutterBottom>
										{progress} of {totalPairs} clones complete
									</Mui.Typography>
									<Mui.LinearProgress
										variant="determinate"
										value={totalPairs > 0 ? (progress / totalPairs) * 100 : 0}
										style={{ height: 8, borderRadius: 4, marginBottom: 8 }}
									/>
									<Mui.Typography variant="caption" color="textSecondary">
										{currentOp}
									</Mui.Typography>
								</Mui.Box>
							</AppCard>
						)}

						{runStatus === "done" && (
							<Mui.Box>
								<MuiLab.Alert severity="success">
									Complete — {completedCount} campaign clone{completedCount !== 1 ? "s" : ""} created across {zips.length} market{zips.length !== 1 ? "s" : ""}.
								</MuiLab.Alert>
								<Mui.Box mt={2}>
									<Mui.Button variant="outlined" color="primary" onClick={reset}>
										Start Over
									</Mui.Button>
								</Mui.Box>
							</Mui.Box>
						)}

						{runStatus === "error" && (
							<Mui.Box>
								<MuiLab.Alert severity="error">
									<strong>Halted — {completedCount} of {totalPairs} clones completed before failure.</strong>
									<br />
									<span style={{ whiteSpace: "pre-wrap" }}>{errorMsg}</span>
								</MuiLab.Alert>
								<Mui.Box mt={2}>
									<Mui.Button variant="outlined" color="primary" onClick={reset}>
										Start Over
									</Mui.Button>
								</Mui.Box>
							</Mui.Box>
						)}
					</Mui.Box>
				</Mui.Container>
			</DashboardLayout>
		);
	}

	// ── Idle / Confirming ────────────────────────────────────────────────────
	return (
		<DashboardLayout permitted={true} title="Zip Campaign Utility" header={pageHeader}>
			<Mui.Container maxWidth="md">
				<Mui.Box pt={3} pb={8}>

					{/* ── Step 1: Zip Codes ─────────────────────────────────── */}
					<Mui.Box mb={2}><AppCard>
						<Mui.Box
							px={2}
							py={1.5}
							display="flex"
							alignItems="center"
							style={{ borderBottom: `1px solid ${DIVIDER}`, gap: 10 }}
						>
							<Mui.Avatar style={{ width: 24, height: 24, fontSize: 12, background: PRIMARY }}>
								1
							</Mui.Avatar>
							<Mui.Typography variant="overline" style={{ fontWeight: 600 }}>
								Enter Zip Codes
							</Mui.Typography>
						</Mui.Box>
						<Mui.Box p={3}>
							<MuiLab.Alert severity="info" style={{ marginBottom: 16 }}>
								Paste one zip code per line, or separate with commas or spaces. Duplicates and blank lines are ignored automatically.
							</MuiLab.Alert>

							<Mui.Box display="flex" style={{ gap: 12, marginBottom: 12 }}>
								<Mui.TextField
									multiline
									rows={5}
									fullWidth
									variant="outlined"
									size="small"
									placeholder={"94105\n94107\n94110\n..."}
									value={zipInput}
									onChange={(e) => handleZipChange(e.target.value)}
									inputProps={{ style: { fontFamily: "monospace", fontSize: 13 } }}
								/>
								<Mui.Box display="flex" flexDirection="column" style={{ gap: 8 }}>
									<Mui.Button size="small" variant="outlined" onClick={() => handleZipChange("")}>
										Clear
									</Mui.Button>
								</Mui.Box>
							</Mui.Box>

							<Mui.Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 10 }}>
								Market name format:{" "}
								<code style={{ background: "#F0F4FF", padding: "1px 5px", borderRadius: 3, color: PRIMARY, fontSize: 11 }}>
									Zip 94105
								</code>
							</Mui.Typography>

							<Mui.Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 8 }}>
								Parsed zip codes ({zips.length}) — optionally name each market area
							</Mui.Typography>
							{zips.length === 0 ? (
								<Mui.Typography variant="caption" color="textSecondary" style={{ fontStyle: "italic" }}>
									No valid zip codes yet.
								</Mui.Typography>
							) : (
								<Mui.Box style={{ display: "flex", flexDirection: "column", gap: 8 }}>
									{zips.map((zip) => (
										<Mui.Box key={zip} display="flex" alignItems="center" style={{ gap: 8 }}>
											<Mui.Typography
												variant="body2"
												style={{ fontFamily: "monospace", fontWeight: 600, color: PRIMARY, width: 56, flexShrink: 0 }}
											>
												{zip}
											</Mui.Typography>
											<Mui.TextField
												size="small"
												variant="outlined"
												fullWidth
												placeholder={`Zip ${zip}`}
												value={zipNames[zip] ?? ""}
												onChange={(e) => handleZipNameChange(zip, e.target.value)}
												inputProps={{ style: { fontSize: 13 } }}
											/>
											<Mui.IconButton size="small" onClick={() => removeZip(zip)}>
												<FeatherIcon><Icons.X /></FeatherIcon>
											</Mui.IconButton>
										</Mui.Box>
									))}
								</Mui.Box>
							)}
						</Mui.Box>
					</AppCard></Mui.Box>

					{/* ── Step 2: Campaign Selector ─────────────────────────── */}
					<Mui.Box mb={2}><AppCard>
						<Mui.Box
							px={2}
							py={1.5}
							display="flex"
							alignItems="center"
							style={{ borderBottom: `1px solid ${DIVIDER}`, gap: 10 }}
						>
							<Mui.Avatar style={{ width: 24, height: 24, fontSize: 12, background: PRIMARY }}>
								2
							</Mui.Avatar>
							<Mui.Typography variant="overline" style={{ fontWeight: 600 }}>
								Select Campaigns to Clone
							</Mui.Typography>
						</Mui.Box>
						<Mui.Box p={3}>
							<MuiLab.Alert severity="info" style={{ marginBottom: 16 }}>
								Each selected campaign will be cloned once per zip code. Clone naming:{" "}
								<code style={{ fontSize: 11 }}>[Campaign Name] - [Zip]</code>
							</MuiLab.Alert>

							<Mui.TextField
								fullWidth
								variant="outlined"
								size="small"
								placeholder="Filter campaigns…"
								value={filterTerm}
								onChange={(e) => setFilterTerm(e.target.value)}
								style={{ marginBottom: 12 }}
							/>

							<Mui.Paper variant="outlined" style={{ boxShadow: "none" }}>
								{/* Select All row */}
								<Mui.Box
									display="flex"
									alignItems="center"
									justifyContent="space-between"
									px={2}
									py={1}
									style={{ borderBottom: `1px solid ${DIVIDER}`, background: "#F8F8F8" }}
								>
									<Mui.FormControlLabel
										control={
											<Mui.Checkbox
												size="small"
												checked={
													visibleCampaigns.length > 0 &&
													visibleCampaigns.every((c) => selectedCampaignIds.has(c.id))
												}
												indeterminate={
													visibleCampaigns.some((c) => selectedCampaignIds.has(c.id)) &&
													!visibleCampaigns.every((c) => selectedCampaignIds.has(c.id))
												}
												onChange={toggleAll}
												color="primary"
											/>
										}
										label={
											<Mui.Typography variant="caption" color="textSecondary" style={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
												Select all visible
											</Mui.Typography>
										}
									/>
									<Mui.Typography variant="caption" color="textSecondary">
										<strong style={{ color: PRIMARY }}>{selectedCampaignIds.size}</strong>{" "}
										of {allCampaigns.length} selected
									</Mui.Typography>
								</Mui.Box>

								{/* Campaign rows */}
								<Mui.Box style={{ maxHeight: 280, overflowY: "auto" }}>
									{visibleCampaigns.length === 0 ? (
										<Mui.Box p={2}>
											<Mui.Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
												No campaigns match your filter.
											</Mui.Typography>
										</Mui.Box>
									) : (
										visibleCampaigns.map((c, idx) => (
											<Mui.Box
												key={c.id}
												display="flex"
												alignItems="center"
												px={2}
												py={1}
												onClick={() => toggleCampaign(c.id)}
												style={{
													cursor: "pointer",
													borderBottom: idx < visibleCampaigns.length - 1 ? `1px solid ${DIVIDER}` : "none",
													background: selectedCampaignIds.has(c.id) ? "rgba(54, 116, 123, 0.08)" : undefined,
												}}
											>
												<Mui.Checkbox
													size="small"
													checked={selectedCampaignIds.has(c.id)}
													color="primary"
													style={{ padding: 4, marginRight: 8 }}
												/>
												<Mui.Box flex={1} minWidth={0}>
													<Mui.Typography variant="body2" style={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
														{c.name}
													</Mui.Typography>
													<Mui.Typography variant="caption" color="textSecondary">
														{Object.values(campaignCampaignMsgById).filter((ccm) => ccm.campaignId === c.id).length} message template(s)
													</Mui.Typography>
												</Mui.Box>
												{c.status === "Inactive" ? (
													<Mui.Chip label="inactive" size="small" variant="outlined" />
												) : (
													<Mui.Chip label="active" size="small" color="secondary" />
												)}
											</Mui.Box>
										))
									)}
								</Mui.Box>
							</Mui.Paper>
						</Mui.Box>
					</AppCard></Mui.Box>

					{/* ── Step 3: Review & Run ──────────────────────────────── */}
					<Mui.Box mb={2}><AppCard>
						<Mui.Box
							px={2}
							py={1.5}
							display="flex"
							alignItems="center"
							style={{ borderBottom: `1px solid ${DIVIDER}`, gap: 10 }}
						>
							<Mui.Avatar style={{ width: 24, height: 24, fontSize: 12, background: PRIMARY }}>
								3
							</Mui.Avatar>
							<Mui.Typography variant="overline" style={{ fontWeight: 600 }}>
								Review &amp; Run
							</Mui.Typography>
						</Mui.Box>

						<Mui.Box p={3} pb={0}>
							{/* Summary grid */}
							<Mui.Grid container style={{ marginBottom: 20 }}>
								{[
									{ num: zips.length, label: "Markets", sub: "created for your areas", accent: true },
									{ num: selectedCampaignIds.size, label: "Campaigns", sub: "selected for cloning", accent: true },
									{ num: totalPairs, label: "Total Clones", sub: selectedCampaignIds.size > 0 ? `${selectedCampaignIds.size} per market` : "—", accent: false },
								].map(({ num, label, sub, accent }, i) => (
									<Mui.Grid item xs={4} key={i}>
										<Mui.Box p={3} style={{ borderRight: i < 2 ? `1px solid ${DIVIDER}` : "none" }}>
											<Mui.Typography
												variant="h4"
												style={{ fontWeight: 700, color: accent ? PRIMARY : undefined, lineHeight: 1, marginBottom: 4 }}
											>
												{num}
											</Mui.Typography>
											<Mui.Typography variant="body2" color="textSecondary" style={{ fontWeight: 500 }}>
												{label}
											</Mui.Typography>
											<Mui.Typography variant="caption" color="textSecondary">
												{sub}
											</Mui.Typography>
										</Mui.Box>
									</Mui.Grid>
								))}
							</Mui.Grid>

							{/* Preview table */}
							{canRun ? (
								<Mui.Box mb={2}>
									<Mui.Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 8 }}>
										Preview (first 6 clones)
									</Mui.Typography>
									<Mui.TableContainer component={Mui.Paper} style={{ boxShadow: "none" }}>
										<Mui.Table size="small">
											<Mui.TableHead>
												<Mui.TableRow>
													{["Zip", "Source Campaign", "Clone Name"].map((h) => (
														<Mui.TableCell key={h} style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>
															{h}
														</Mui.TableCell>
													))}
												</Mui.TableRow>
											</Mui.TableHead>
											<Mui.TableBody>
												{previewPairs.slice(0, 6).map((p, i) => (
													<Mui.TableRow key={i}>
														<Mui.TableCell style={{ fontFamily: "monospace", fontWeight: 600, color: PRIMARY, fontSize: 12 }}>
															{p.zip}
														</Mui.TableCell>
														<Mui.TableCell style={{ fontFamily: "monospace", fontSize: 12 }}>
															{p.src}
														</Mui.TableCell>
														<Mui.TableCell style={{ fontFamily: "monospace", fontSize: 12 }}>
															{p.src} - {p.zip}
														</Mui.TableCell>
													</Mui.TableRow>
												))}
											</Mui.TableBody>
										</Mui.Table>
										{previewPairs.length > 6 && (
											<Mui.Box p={1} textAlign="center" style={{ borderTop: `1px solid ${DIVIDER}`, background: "#F8F8F8" }}>
												<Mui.Typography variant="caption" color="textSecondary" style={{ fontStyle: "italic" }}>
													+ {previewPairs.length - 6} more clone{previewPairs.length - 6 !== 1 ? "s" : ""}…
												</Mui.Typography>
											</Mui.Box>
										)}
									</Mui.TableContainer>
								</Mui.Box>
							) : (
								<Mui.Box py={3} textAlign="center">
									<Mui.Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
										Add zip codes and select at least one campaign to see a preview.
									</Mui.Typography>
								</Mui.Box>
							)}
						</Mui.Box>

						{/* Action row */}
						<Mui.Box
							display="flex"
							justifyContent="flex-end"
							alignItems="center"
							style={{ gap: 10, padding: "16px 20px", borderTop: `1px solid ${DIVIDER}`, background: "#F8F8F8" }}
						>
							<Mui.Button variant="outlined" color="primary" size="small" onClick={reset}>
								Reset
							</Mui.Button>
							<Mui.Button
								variant="contained"
								color="primary"
								size="small"
								disabled={!canRun}
								onClick={() => setRunStatus("confirming")}
							>
								Run Bulk Creation →
							</Mui.Button>
						</Mui.Box>
					</AppCard></Mui.Box>

					{/* ── Confirmation card ─────────────────────────────────── */}
					{runStatus === "confirming" && (
						<AppCard>
							<Mui.Box p={3}>
								<MuiLab.Alert severity="warning" style={{ marginBottom: 16 }}>
									<strong>This will create real records.</strong>{" "}
									{totalPairs} campaign clone{totalPairs !== 1 ? "s" : ""} across {zips.length} market{zips.length !== 1 ? "s" : ""} will be created. This cannot be undone from this utility.
								</MuiLab.Alert>
								<Mui.Box display="flex" justifyContent="flex-end" style={{ gap: 10 }}>
									<Mui.Button variant="outlined" color="primary" size="small" onClick={() => setRunStatus("idle")}>
										Cancel
									</Mui.Button>
									<Mui.Button
										variant="outlined"
										size="small"
										style={{ color: "#E53E3E", borderColor: "#FEB2B2" }}
										onClick={runBulkCreation}
									>
										Yes, Create Records
									</Mui.Button>
								</Mui.Box>
							</Mui.Box>
						</AppCard>
					)}
				</Mui.Box>
			</Mui.Container>
		</DashboardLayout>
	);
};

const mapStateToProps = (state: RootState) => ({
	reduxState: state,
});

export const ZipCampaignUtilityPage = connect(mapStateToProps)(Component);
